import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Users, Layers, Calendar } from "lucide-react";
import { useProductionMachines } from "../hooks/useProductionMachines";
import { t } from "@/shared/lib/th-translations";
import {
  computeZScore,
  zScoreColor,
  hasEnoughBaseline,
  formatMetricDisplay,
} from "@/frontend/lib/production-stats";
import { groupMachinesByCategory } from "@/frontend/lib/machine-categories";
import type { MachineActivitySummary } from "@/shared/types/production";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription } from "../components/ui/alert";
import ZScoreLegend from "../components/production/ZScoreLegend";
import MachineContributionsModal from "../components/production/MachineContributionsModal";

type WindowDays = 1 | 7 | 14 | 30;

const windowOptions: { label: string; value: WindowDays }[] = [
  { label: "production.machines.window.today", value: 1 },
  { label: "production.machines.window.last7Days", value: 7 },
  { label: "production.machines.window.last14Days", value: 14 },
  { label: "production.machines.window.last30Days", value: 30 },
];

type MachineMetric = "good_units" | "shots" | "value" | "paper_length" | "paper_area";

const machineMetricOptions: { label: string; value: MachineMetric }[] = [
  { label: "production.machines.metric.goodUnits", value: "good_units" },
  { label: "production.machines.metric.shots", value: "shots" },
  { label: "production.machines.metric.value", value: "value" },
  { label: "production.machines.metric.paperLength", value: "paper_length" },
  { label: "production.machines.metric.paperArea", value: "paper_area" },
];

const THB_FORMATTER = new Intl.NumberFormat("th-TH", {
  style: "currency",
  currency: "THB",
  maximumFractionDigits: 0,
});

function formatTHB(n: number): string {
  return THB_FORMATTER.format(n);
}

export default function ProductionMachines() {
  const [windowDays, setWindowDays] = useState<WindowDays>(14);
  const [metric, setMetric] = useState<MachineMetric>("shots");
  const [drill, setDrill] = useState<
    { machine: MachineActivitySummary; day: string | null } | null
  >(null);
  const { data, isLoading, error } = useProductionMachines(windowDays);

  const responseData = data?.data;
  const days = responseData?.days ?? [];
  const machines = useMemo(
    () =>
      [...(responseData?.machines ?? [])].sort((a, b) =>
        a.machine.localeCompare(b.machine, "th", { numeric: true }),
      ),
    [responseData],
  );

  const totalMachines = responseData?.totalMachines ?? 0;
  const totalEvents = responseData?.totalEvents ?? 0;
  const avgActiveDays = useMemo(() => {
    if (machines.length === 0) return 0;
    return machines.reduce((sum, m) => sum + m.activeDays, 0) / machines.length;
  }, [machines]);

  const groups = useMemo(() => groupMachinesByCategory(machines), [machines]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">{t("navigation.productionMachines")}</h1>
        <p className="text-muted-foreground">{t("production.machines.subtitle")}</p>
      </div>

      {/* Caveat */}
      <Alert>
        <AlertDescription className="text-sm text-muted-foreground">
          {t("production.machines.caveat")}
        </AlertDescription>
      </Alert>

      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t("production.machines.window.label")}:
          </span>
          <div className="flex gap-1">
            {windowOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={windowDays === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setWindowDays(opt.value)}
              >
                {t(opt.label)}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t("production.machines.metric.label")}:
          </span>
          <div className="flex gap-1">
            {machineMetricOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={metric === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setMetric(opt.value)}
              >
                {t(opt.label)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.machines.stats.totalMachines")}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                totalMachines.toLocaleString()
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.machines.stats.totalEvents")}
            </CardTitle>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                totalEvents.toLocaleString()
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.machines.stats.avgActiveDays")}
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                avgActiveDays.toFixed(1)
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{t("production.machines.error")}</AlertDescription>
        </Alert>
      )}

      {/* Main heatmap table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded" />
          ))}
        </div>
      ) : machines.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          {t("production.machines.empty")}
        </div>
      ) : (
        <div className="space-y-4">
          <ZScoreLegend namespace="production.machines.legend" showNoData />
          {groups.map(({ category, items }) => (
            <Card key={category.id}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="text-xl" aria-hidden>
                    {category.icon}
                  </span>
                  <span>{category.label}</span>
                  <span className="text-xs font-normal text-muted-foreground">
                    ({items.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <MachineHeatmapTable
                  machines={items}
                  days={days}
                  metric={metric}
                  onOpenDrill={(machine, day) => setDrill({ machine, day })}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {drill && (
        <MachineContributionsModal
          open
          onClose={() => setDrill(null)}
          machine={drill.machine.machine}
          day={drill.day}
          rows={drill.machine.contributions}
        />
      )}
    </div>
  );
}

interface HeatmapTableProps {
  machines: MachineActivitySummary[];
  days: string[];
  metric: MachineMetric;
  onOpenDrill?: (machine: MachineActivitySummary, day: string | null) => void;
}

function machineDailyVal(m: MachineActivitySummary, day: string, metric: MachineMetric): number {
  if (metric === "shots") return m.dailyShots?.[day] ?? 0;
  if (metric === "value") return m.dailyValue?.[day] ?? 0;
  if (metric === "paper_length") return m.dailyPaperLength?.[day] ?? 0;
  if (metric === "paper_area") return m.dailyPaperArea?.[day] ?? 0;
  return m.dailyGoodUnits[day] ?? 0;
}

function machineTotalVal(m: MachineActivitySummary, metric: MachineMetric): number {
  if (metric === "shots") return m.totalShots;
  if (metric === "value") return m.totalValue;
  if (metric === "paper_length") return m.totalPaperLength;
  if (metric === "paper_area") return m.totalPaperArea;
  return m.totalGoodUnits;
}

function machineBaselineRef(m: MachineActivitySummary, metric: MachineMetric) {
  if (metric === "shots") return m.baselineShots;
  if (metric === "paper_length") return m.baselinePaperLength;
  if (metric === "paper_area") return m.baselinePaperArea;
  return m.baseline;
}

const NO_ZSCORE_METRICS = new Set<MachineMetric>(["value"]);

function MachineHeatmapTable({ machines, days, metric, onOpenDrill }: HeatmapTableProps) {
  const dailySums: Record<string, number> = {};
  let grandTotal = 0;
  for (const day of days) dailySums[day] = 0;
  for (const m of machines) {
    for (const day of days) {
      dailySums[day] += machineDailyVal(m, day, metric);
    }
    grandTotal += machineTotalVal(m, metric);
  }
  const fmtCell = (v: number) =>
    metric === "value" ? formatTHB(v) : formatMetricDisplay(v, metric);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="sticky left-0 bg-gray-50 px-4 py-2 text-left font-medium whitespace-nowrap z-10 border-r">
              {t("production.machines.table.machineCol")}
            </th>
            {days.map((day) => (
              <th
                key={day}
                className="px-2 py-2 text-center font-medium whitespace-nowrap min-w-[56px]"
              >
                {day.slice(5)}
              </th>
            ))}
            <th className="px-4 py-2 text-right font-medium whitespace-nowrap border-l">
              {t("production.machines.table.totalCol")}
            </th>
          </tr>
        </thead>
        <tbody>
          {machines.map((machine) => (
            <tr key={machine.machine} className="border-b hover:bg-gray-50/50">
              <td className="sticky left-0 bg-white px-4 py-2 whitespace-nowrap z-10 border-r">
                <div className="font-medium">
                  <Link
                    to={`/production/machines/${encodeURIComponent(machine.machine)}`}
                    className="hover:underline text-blue-600"
                  >
                    {machine.machine}
                  </Link>
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("production.machines.table.operatorsCol")}: {machine.uniqueOperators}
                  {" · "}
                  {t("production.machines.table.activeDaysCol")}: {machine.activeDays}
                </div>
              </td>

              {days.map((day) => {
                const cellValue = machineDailyVal(machine, day, metric);
                const hasData = cellValue > 0;

                if (!hasData) {
                  return (
                    <td
                      key={day}
                      className="px-2 py-2 text-center bg-gray-100 text-gray-400 text-xs"
                      title={t("production.machines.table.emptyDay")}
                    >
                      —
                    </td>
                  );
                }

                const baselineRef = machineBaselineRef(machine, metric);
                const enough = hasEnoughBaseline(baselineRef.count);
                const skipZScore = NO_ZSCORE_METRICS.has(metric);
                const z = enough && !skipZScore
                  ? computeZScore(cellValue, baselineRef.mean, baselineRef.stddev)
                  : 0;
                const colorClass = skipZScore
                  ? "bg-emerald-50 text-emerald-900"
                  : enough
                    ? zScoreColor(z)
                    : "bg-gray-200 text-gray-700";
                const valueLabel = t(`production.machines.metric.${
                  metric === "good_units" ? "goodUnits" : metric === "paper_length" ? "paperLength" : metric === "paper_area" ? "paperArea" : metric
                }`);
                const cellDisplay =
                  metric === "value"
                    ? formatTHB(cellValue)
                    : formatMetricDisplay(cellValue, metric);

                const tooltipLines = [
                  `${t("production.machines.tooltip.day")}: ${day}`,
                  `${t("production.machines.tooltip.machine")}: ${machine.machine}`,
                  `${valueLabel}: ${cellDisplay}`,
                  enough && !skipZScore
                    ? `${t("production.machines.tooltip.ownMean")}: ${formatMetricDisplay(baselineRef.mean, metric)}`
                    : null,
                  enough && metric !== "value"
                    ? `${t("production.machines.tooltip.zScore")}: ${z.toFixed(2)}`
                    : null,
                ]
                  .filter(Boolean)
                  .join("\n");

                return (
                  <td
                    key={day}
                    className={`px-2 py-2 text-center text-xs font-medium ${colorClass} ${onOpenDrill ? "cursor-pointer hover:ring-2 hover:ring-blue-400" : ""}`}
                    title={tooltipLines}
                    onClick={onOpenDrill ? () => onOpenDrill(machine, day) : undefined}
                  >
                    {cellDisplay}
                  </td>
                );
              })}

              <td
                className={`px-4 py-2 text-right font-semibold whitespace-nowrap border-l ${onOpenDrill ? "cursor-pointer hover:bg-blue-50" : ""}`}
                onClick={onOpenDrill ? () => onOpenDrill(machine, null) : undefined}
              >
                {metric === "value"
                  ? formatTHB(machineTotalVal(machine, metric))
                  : formatMetricDisplay(machineTotalVal(machine, metric), metric)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 bg-gray-100 font-semibold">
            <td className="sticky left-0 bg-gray-100 px-4 py-2 whitespace-nowrap z-10 border-r">
              {t("production.machines.table.sumRow")}
            </td>
            {days.map((day) => (
              <td
                key={day}
                className="px-2 py-2 text-center text-xs whitespace-nowrap tabular-nums"
              >
                {dailySums[day] > 0 ? fmtCell(dailySums[day]) : "—"}
              </td>
            ))}
            <td className="px-4 py-2 text-right whitespace-nowrap border-l tabular-nums">
              {fmtCell(grandTotal)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
